// lifetime-regions-v0 reader (ba-life's ENIGMA-PD stage maps, docs/contracts/lifetime-regions-v0.md): signed per-structure
// effects on the MNI pack's DKT structures, in two tracks over the same ordinal stages. Measured: four published group
// snapshots (Hoehn and Yahr 1, 2, 3, 4/5), shown one exact stage at a time, never interpolated. Model: a frozen connectome
// diffusion fit's 121 samples over ordinal steps 0–3. A step is a stage, never age or elapsed time. The app reads the run and
// drives the viewport's `overlay` with one frame at a time (features/lifetime.ts); by design no engine `sim` reader takes it.
// Mask 0 means no data (NaN in a frame keeps the structure's base colour); six DK-only regions have no DKT counterpart and
// are never assigned a neighbour.

import type { ArrayDescriptor } from './binary.ts';
import type { SimFiles } from './files.ts';

export interface RegionsTrackHeader {
  label: string;
  kind?: string;
  steps: number[];
  stage_labels?: string[];
  values: ArrayDescriptor;
  valid: ArrayDescriptor;
  interpolation?: string;
  source_operation?: string;
  [k: string]: unknown;
}

export interface RegionsHeader {
  format: 'lifetime-regions-v0';
  id: string;
  kind?: string;
  provenance?: string;
  space?: string;
  pack: string;
  structure_ids: string[];
  units?: Record<string, string>;
  time?: { base?: string; range?: [number, number]; origin?: string; viewport_clock_units_per_step?: number };
  unmapped?: { dk_region: string; structure_id: string | null; relation?: string; no_data_reason?: string }[];
  mapping?: string;
  tracks: { measured: RegionsTrackHeader; model: RegionsTrackHeader };
  model?: { name?: string; equation?: string; parameters?: Record<string, unknown>; fit_stage?: string; [k: string]: unknown };
  display: { shared_range: [number, number]; colormap?: string; missing?: string; api?: string; [k: string]: unknown };
  validation?: { verdict?: string; primary?: Record<string, { rmse?: number; nrmse?: number; spearman?: number; target_population_sd?: number }>; [k: string]: unknown };
  methods?: string;
  inputs?: Record<string, unknown>;
  pipeline?: Record<string, unknown>;
  files?: Record<string, string>;
  [k: string]: unknown;
}

/** One track's frames: `[frame][structure]` values with their mask. */
export class RegionsTrack {
  constructor(
    readonly header: RegionsTrackHeader,
    /** Structures per frame. */
    readonly width: number,
    private readonly values: Float32Array,
    private readonly valid: Uint8Array,
  ) {}

  get steps(): number[] {
    return this.header.steps;
  }

  get frames(): number {
    return this.header.steps.length;
  }

  /** Frame i's value per structure; NaN where the mask says no data. A fresh array each time: the viewport copies what it's sent. */
  frame(i: number): Float32Array {
    if (!(i >= 0 && i < this.frames)) throw new RangeError(`frame ${i} of ${this.frames}`);
    const n = this.width;
    const out = new Float32Array(n);
    for (let k = 0; k < n; k++) out[k] = this.valid[i * n + k] ? this.values[i * n + k]! : NaN;
    return out;
  }

  /** The index of the frame whose step is nearest `step` (a tie goes to the later one); the first for a non-number. */
  nearest(step: number): number {
    if (!Number.isFinite(step)) return 0;
    let best = 0;
    for (let i = 1; i < this.steps.length; i++) if (Math.abs(this.steps[i]! - step) <= Math.abs(this.steps[best]! - step)) best = i;
    return best;
  }
}

export class RegionsSim {
  private constructor(
    readonly files: SimFiles,
    readonly header: RegionsHeader,
    readonly measured: RegionsTrack,
    readonly model: RegionsTrack,
  ) {}

  static async open(files: SimFiles): Promise<RegionsSim> {
    const h = await files.json<RegionsHeader>('header.json');
    if (h.format !== 'lifetime-regions-v0') throw new Error(`sims/${files.dir}: ${String(h.format)}, not lifetime-regions-v0`);
    const n = h.structure_ids?.length ?? 0;
    if (!n) throw new Error(`sims/${files.dir}: no structure_ids`);
    if (!h.tracks?.measured || !h.tracks?.model) throw new Error(`sims/${files.dir}: a run without both tracks`);
    const r = h.display?.shared_range;
    if (!Array.isArray(r) || r.length !== 2 || !(r[0] < r[1])) throw new Error(`sims/${files.dir}: display.shared_range isn’t a range`);
    const track = async (t: RegionsTrackHeader, name: string): Promise<RegionsTrack> => {
      if (!Array.isArray(t.steps) || !t.steps.length) throw new Error(`sims/${files.dir}: ${name} has no steps`);
      const [v, m] = await Promise.all([files.array(t.values), files.array(t.valid)]);
      if (!(v instanceof Float32Array) || !(m instanceof Uint8Array)) throw new Error(`sims/${files.dir}: ${name}’s arrays aren’t f32 values and u1 masks`);
      if (v.length !== t.steps.length * n || m.length !== v.length) throw new Error(`sims/${files.dir}: ${name} is ${v.length} values for ${t.steps.length} × ${n}`);
      return new RegionsTrack(t, n, v, m);
    };
    const [measured, model] = await Promise.all([track(h.tracks.measured, 'measured'), track(h.tracks.model, 'model')]);
    return new RegionsSim(files, h, measured, model);
  }

  get structureIds(): string[] {
    return this.header.structure_ids;
  }

  /** The fixed signed range both tracks share, d. */
  get range(): [number, number] {
    return [this.header.display.shared_range[0], this.header.display.shared_range[1]];
  }

  /** The measured stages' labels (HY1, HY2, HY3, HY4/5), one per measured step. */
  get stageLabels(): string[] {
    const t = this.header.tracks.measured;
    return t.stage_labels && t.stage_labels.length === t.steps.length ? t.stage_labels : t.steps.map((s) => `stage ${s}`);
  }

  /** The regions scored but not displayable: DK regions with no DKT counterpart, by their DK names. */
  get noData(): string[] {
    return (this.header.unmapped ?? []).filter((u) => u.structure_id === null).map((u) => u.dk_region);
  }
}

/** A stage label in words. */
export function stageName(label: string): string {
  const m = /^HY(\d)(?:\/(\d))?$/.exec(label);
  if (!m) return label;
  return m[2] ? `Hoehn and Yahr stages ${m[1]} and ${m[2]}` : `Hoehn and Yahr stage ${m[1]}`;
}

/** The structures with the most and the least thinning in a frame (NaN skipped); null where nothing is finite. */
export function extremes(frame: ArrayLike<number>, ids: readonly string[]): { most: { id: string; value: number } | null; least: { id: string; value: number } | null; finite: number } {
  let most: { id: string; value: number } | null = null;
  let least: { id: string; value: number } | null = null;
  let finite = 0;
  for (let k = 0; k < frame.length && k < ids.length; k++) {
    const v = frame[k]!;
    if (!Number.isFinite(v)) continue;
    finite++;
    if (!most || v > most.value) most = { id: ids[k]!, value: v };
    if (!least || v < least.value) least = { id: ids[k]!, value: v };
  }
  return { most, least, finite };
}

/** A DK region's name for the panel ("L_bankssts" → "left banks of the superior temporal sulcus"). */
export function dkRegionName(dk: string): string {
  const m = /^([LR])_(.+)$/.exec(dk);
  const side = m ? (m[1] === 'L' ? 'left ' : 'right ') : '';
  const base = (m ? m[2]! : dk).toLowerCase();
  const words: Record<string, string> = { bankssts: 'banks of the superior temporal sulcus', frontalpole: 'frontal pole', temporalpole: 'temporal pole' };
  return `${side}${words[base] ?? base}`;
}
